import React, { useEffect, useState } from 'react';
import api from '../api/axios';
import useAuthStore from '../store/authStore';
import { mediaSrc } from '../api/media';
import { Sticker } from '../components/Cartoons';

interface MediaItem {
  id: number;
  type: 'PHOTO' | 'VIDEO';
  filename: string;
  description: string | null;
  url: string;
  uploadedAt: string;
}

interface KidMediaProps {
  type: 'PHOTO' | 'VIDEO';
}

const KidMedia: React.FC<KidMediaProps> = ({ type }) => {
  const { userId } = useAuthStore();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);

  const isPhoto = type === 'PHOTO';
  const title = isPhoto ? 'Images' : 'Videos';
  const emoji = isPhoto ? '📷' : '🎬';

  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/api/kids/${userId}/media`, { params: { type } });
        setItems(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchMedia();
  }, [userId, type]);

  return (
    <div className="space-y-5">
      <div className="kt-card p-4 flex items-center gap-3">
        <Sticker emoji={emoji} from={isPhoto ? '#A7F3D0' : '#BAE6FD'} to={isPhoto ? '#6EE7B7' : '#7DD3FC'} size={48} />
        <div>
          <h1 className="text-lg font-display font-extrabold text-gray-800">{title}</h1>
          <p className="text-xs text-gray-500">Shared with you by family &amp; friends</p>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm text-center py-6">Loading…</p>
      ) : items.length === 0 ? (
        <div className="kt-card p-10 text-center text-gray-400">
          No {title.toLowerCase()} shared with you yet.
        </div>
      ) : isPhoto ? (
        <div className="grid grid-cols-2 gap-3">
          {items.map((m) => (
            <div key={m.id} className="kt-card overflow-hidden">
              <a href={mediaSrc(m.url)} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden">
                <img src={mediaSrc(m.url)} alt={m.description || m.filename}
                     className="w-full h-full object-cover hover:scale-105 transition" />
              </a>
              <p className="px-3 py-2 text-xs font-semibold text-gray-600 truncate">
                {m.description || m.filename}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((m) => (
            <div key={m.id} className="kt-card p-3">
              <video src={mediaSrc(m.url)} controls className="w-full rounded-xl max-h-[70vh] bg-black" />
              <p className="text-sm font-semibold text-gray-700 mt-2">{m.description || m.filename}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default KidMedia;
